// src/components/dashboard/TransferPaymentModal.jsx




// src/components/dashboard/TransferPaymentModal.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useAuth } from "../../hooks/useAuth";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

/** Pricing:
 *  1 transfer = $19.95
 *  + $0.20 for each additional listing (numListings - 1)
 */
const BASE_PRICE = 19.95;
const EXTRA_UNIT_PRICE = 0.2;
const calcPrice = (count = 1) =>
  Number((BASE_PRICE + Math.max(0, count - 1) * EXTRA_UNIT_PRICE).toFixed(2));

/* ─────────────────── ENV COUPON (trim + uppercase for safe compare) ─────────────────── */
const COUPON_CODE = (process.env.REACT_APP_COUPON_CODE || "Freeaccess2026")
  .trim()
  .toUpperCase();

const TransferPaymentModal = ({
  open,
  handleClose,
  numListings = 1,
  payerEmail: payerEmailProp,
  onSuccess,
  onPaymentSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const firestore = getFirestore();

  const notifySuccess = useMemo(() => onSuccess || onPaymentSuccess || (() => { }), [onSuccess, onPaymentSuccess]);
  const initialEmail = payerEmailProp || currentUser?.email || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stripe-related state
  const [clientSecret, setClientSecret] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [requiresCard, setRequiresCard] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);

  // General UI state
  const [email, setEmail] = useState(initialEmail);
  const [price, setPrice] = useState(calcPrice(numListings));
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Coupon/free state
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [freeActive, setFreeActive] = useState(false);
  const [freeCheckDone, setFreeCheckDone] = useState(false); // NEW: gate Stripe calls

  // Always localhost fallback
  const API_URL = process.env.REACT_APP_API_URL ;
  //  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    setPrice(calcPrice(numListings));
  }, [numListings]);

  useEffect(() => {
    if (payerEmailProp && payerEmailProp !== email) setEmail(payerEmailProp);
  }, [payerEmailProp]); // eslint-disable-line

  /* ───────── Check Firestore for prior coupon/free flag on open ───────── */
  useEffect(() => {
    let cancelled = false;
    const checkFree = async () => {
      if (!open || !currentUser?.uid) return;
      try {
        const snap = await getDoc(doc(firestore, "users", currentUser.uid));
        if (cancelled) return;

        const data = snap.exists() ? snap.data() : {};
        const now = Date.now();

        // Handle number or Timestamp
        const until =
          typeof data?.freeListingUntil === "number"
            ? data.freeListingUntil
            : data?.freeListingUntil?.toMillis
              ? data.freeListingUntil.toMillis()
              : 0;

        const isFree = until > now;
        setFreeActive(isFree);

        if (isFree) {
          setPaymentSuccess(true);
          // Short success and exit—NO Stripe calls
          setTimeout(() => {
            notifySuccess();
            handleClose();
          }, 1200);
        }
      } catch (e) {
        console.warn("[WARN] Could not check user free flag:", e);
        setFreeActive(false);
      } finally {
        if (!cancelled) setFreeCheckDone(true);
      }
    };

    if (open) {
      setFreeCheckDone(false);
      checkFree();
    }

    return () => {
      cancelled = true;
    };
  }, [open, currentUser?.uid, firestore, handleClose, notifySuccess]);

  /* ───────── fetch stored customerId when modal opens (ONLY if not free) ───────── */
  useEffect(() => {
    if (!open || !freeCheckDone || freeActive) return;

    const ac = new AbortController();
    const fetchCustomerId = async () => {
      try {
        const resp = await fetch(
          `${API_URL}/api/payments/get-customer-id?email=${encodeURIComponent(
            email
          )}`,
          { signal: ac.signal }
        );
        if (!resp.ok) throw new Error("network");
        const data = await resp.json();
        if (ac.signal.aborted) return;
        if (data.customerId) {
          setCustomerId(data.customerId);
          console.log(`[INFO] Retrieved Stripe customer ID: ${data.customerId}`);
        } else {
          console.log("[INFO] No stored Stripe customer ID, creating new one.");
          setCustomerId(null);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[ERROR] Failed to fetch customer ID:", err);
      }
    };

    fetchCustomerId();
    return () => ac.abort();
  }, [open, email, API_URL, freeActive, freeCheckDone]);

  /* ───────── fetch payment intent (ONLY if not free) ───────── */
  useEffect(() => {
    if (!(open && freeCheckDone && !freeActive && customerId)) return;

    const ac = new AbortController();
    const run = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/create-payment-intent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: price,
            currency: "usd",
            customerId,
            email,
          }),
          signal: ac.signal,
        });
        if (!res.ok) throw new Error("network");
        const data = await res.json();
        if (ac.signal.aborted) return;

        setClientSecret(data.clientSecret || "");
        setRequiresCard(Boolean(data.requiresCard));
        if (data.requiresCard === false) {
          setPaymentSuccess(true);
          setTimeout(() => {
            notifySuccess();
            handleClose();
          }, 2000);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to initialize payment.");
      }
    };
    run();

    return () => ac.abort();
  }, [open, price, customerId, email, API_URL, handleClose, notifySuccess, freeActive, freeCheckDone]);

  /* ───────── Stripe card payment flow (NEVER called if freeActive) ───────── */
  const handlePayment = async () => {
    if (
      freeActive || // guard
      !stripe ||
      !clientSecret ||
      (requiresCard && (!elements || !cardComplete))
    )
      return;

    setLoading(true);
    setError(null);

    try {
      let paymentResult = null;

      if (requiresCard) {
        paymentResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { email },
          },
        });
      }

      if (paymentResult?.error) {
        setError(paymentResult.error.message);
      } else if (
        !requiresCard ||
        paymentResult?.paymentIntent?.status === "succeeded"
      ) {
        setPaymentSuccess(true);

        if (requiresCard && paymentResult?.paymentIntent?.payment_method) {
          fetch(`${API_URL}/api/payments/save-payment-method`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customerId,
              paymentMethodId: paymentResult.paymentIntent.payment_method,
              email,
            }),
          }).catch(() => { });
        }

        setTimeout(() => {
          notifySuccess();
          handleClose();
        }, 2000);
      }
    } catch {
      setError("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ───────── coupon redemption (NO STRIPE INTERACTION) ───────── */
  const handleCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (code !== COUPON_CODE) {
      setCouponMsg("Invalid coupon");
      return;
    }
    try {
      setLoading(true);

      // Cancel any in-flight Stripe requests immediately
      setClientSecret("");
      setCustomerId(null);

      const freeUntil = Date.now() + 365 * 24 * 60 * 60 * 1000; // +1 year
      await setDoc(
        doc(firestore, "users", currentUser.uid),
        {
          freeListingUntil: freeUntil,
          couponCode: COUPON_CODE,
        },
        { merge: true }
      );

      // Activate free mode and exit—no Stripe calls anywhere
      setFreeActive(true);
      setPaymentSuccess(true);
      setCouponMsg(null);
      setTimeout(() => {
        notifySuccess();
        handleClose();
      }, 1200);
    } catch (e) {
      console.error(e);
      setCouponMsg("Could not apply coupon. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="transfer-payment-modal">
      <Box
        sx={{
          width: 400,
          p: 3,
          bgcolor: "white",
          borderRadius: 2,
          mx: "auto",
          mt: "20%",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {paymentSuccess ? "Success!" : "Transfer Payment"}
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Total for {numListings} listing{numListings > 1 ? "s" : ""}:{" "}
          <strong>${price.toFixed(2)}</strong>{" "}
          (Base ${BASE_PRICE.toFixed(2)} + ${EXTRA_UNIT_PRICE.toFixed(2)} each additional)
        </Alert>

        {/* ───── coupon UI ───── */}
        <TextField
          fullWidth
          label="Coupon Code"
          value={coupon}
          onChange={(e) => {
            setCoupon(e.target.value);
            setCouponMsg(null);
          }}
          sx={{ mb: 2 }}
          disabled={paymentSuccess || freeActive}
        />
        <Button
          variant="outlined"
          fullWidth
          disabled={!coupon || loading || paymentSuccess || freeActive}
          onClick={handleCoupon}
          sx={{ mb: 2 }}
        >
          Apply Coupon
        </Button>
        {couponMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {couponMsg}
          </Alert>
        )}

        {/* Billing email (disabled like your original) */}
        <TextField
          fullWidth
          label="Billing Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
          required
          disabled
        />

        {/* If freeActive, we never show Stripe UI */}
        {freeActive ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Free access active.
          </Alert>
        ) : requiresCard ? (
          <>
            <CardElement
              options={{ hidePostalCode: true }}
              onChange={(e) => setCardComplete(e.complete)}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handlePayment}
              disabled={loading || !clientSecret || !cardComplete || !freeCheckDone}
              sx={{ mt: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                `Pay $${price.toFixed(2)} & Save Card`
              )}
            </Button>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="success" sx={{ mt: 2 }}>
            Payment successful! Charged automatically.
          </Alert>
        )}
      </Box>
    </Modal>
  );
};

export default TransferPaymentModal;


// import React, { useState, useEffect } from "react";
// import {
//   Modal,
//   Box,
//   Typography,
//   Button,
//   Alert,
//   CircularProgress,
//   TextField,
// } from "@mui/material";
// import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
// import { useAuth } from "../../hooks/useAuth";

// /** Pricing:
//  *  1 transfer = $19.95
//  *  + $0.20 for each additional listing (numListings - 1)
//  */
// const BASE_PRICE = 19.95;
// const EXTRA_UNIT_PRICE = 0.2;
// const calcPrice = (count = 1) =>
//   Number((BASE_PRICE + Math.max(0, count - 1) * EXTRA_UNIT_PRICE).toFixed(2));

// const TransferPaymentModal = ({
//   open,
//   handleClose,
//   numListings = 1,
//   payerEmail: payerEmailProp,
//   onSuccess,
//   onPaymentSuccess,
// }) => {
//   const stripe = useStripe();
//   const elements = useElements();
//   const { currentUser } = useAuth();

//   const notifySuccess = onSuccess || onPaymentSuccess || (() => {});
//   const initialEmail = payerEmailProp || currentUser?.email || "";

//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [clientSecret, setClientSecret] = useState("");
//   const [email, setEmail] = useState(initialEmail);
//   const [price, setPrice] = useState(calcPrice(numListings));
//   const [paymentSuccess, setPaymentSuccess] = useState(false);
//   const [customerId, setCustomerId] = useState(null);
//   const [requiresCard, setRequiresCard] = useState(true);
//   const [cardComplete, setCardComplete] = useState(false);

//   // Always localhost fallback
//   const API_URL = process.env.REACT_APP_API_URL || "http://localhost:9000";
//   //  const API_URL = process.env.REACT_APP_API_URL;

//   useEffect(() => {
//     setPrice(calcPrice(numListings));
//   }, [numListings]);

//   useEffect(() => {
//     if (payerEmailProp && payerEmailProp !== email) setEmail(payerEmailProp);
//   }, [payerEmailProp]); // eslint-disable-line

//   /* ───────── fetch stored customerId when modal opens ───────── */
//   useEffect(() => {
//     if (!open) return;

//     const fetchCustomerId = async () => {
//       try {
//         const resp = await fetch(
//           `${API_URL}/api/payments/get-customer-id?email=${encodeURIComponent(
//             email
//           )}`
//         );
//         const data = await resp.json();
//         if (data.customerId) {
//           setCustomerId(data.customerId);
//           console.log(`[INFO] Retrieved Stripe customer ID: ${data.customerId}`);
//         } else {
//           console.log("[INFO] No stored Stripe customer ID, creating new one.");
//         }
//       } catch (err) {
//         console.error("[ERROR] Failed to fetch customer ID:", err);
//       }
//     };

//     fetchCustomerId();
//   }, [open, email, API_URL]);

//   /* ───────── fetch payment intent once customerId is retrieved ───────── */
//   useEffect(() => {
//     if (!(open && customerId)) return;

//     fetch(`${API_URL}/api/payments/create-payment-intent`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         amount: price,
//         currency: "usd",
//         customerId,
//         email,
//       }),
//     })
//       .then((res) => res.json())
//       .then((data) => {
//         setClientSecret(data.clientSecret || "");
//         setRequiresCard(Boolean(data.requiresCard));
//         if (data.requiresCard === false) {
//           setPaymentSuccess(true);
//           setTimeout(() => {
//             notifySuccess();
//             handleClose();
//           }, 2000);
//         }
//       })
//       .catch(() => setError("Failed to initialize payment."));
//   }, [open, price, customerId, email, API_URL]);

//   /* ───────── Stripe card payment flow ───────── */
//   const handlePayment = async () => {
//     if (
//       !stripe ||
//       !clientSecret ||
//       (requiresCard && (!elements || !cardComplete))
//     )
//       return;

//     setLoading(true);
//     setError(null);

//     try {
//       let paymentResult = null;

//       if (requiresCard) {
//         paymentResult = await stripe.confirmCardPayment(clientSecret, {
//           payment_method: {
//             card: elements.getElement(CardElement),
//             billing_details: { email },
//           },
//         });
//       }

//       if (paymentResult?.error) {
//         setError(paymentResult.error.message);
//       } else if (
//         !requiresCard ||
//         paymentResult.paymentIntent.status === "succeeded"
//       ) {
//         setPaymentSuccess(true);

//         if (requiresCard && paymentResult?.paymentIntent?.payment_method) {
//           fetch(`${API_URL}/api/payments/save-payment-method`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({
//               customerId,
//               paymentMethodId: paymentResult.paymentIntent.payment_method,
//               email,
//             }),
//           }).catch(() => {});
//         }

//         setTimeout(() => {
//           notifySuccess();
//           handleClose();
//         }, 2000);
//       }
//     } catch {
//       setError("Payment failed. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Modal open={open} onClose={handleClose} aria-labelledby="transfer-payment-modal">
//       <Box
//         sx={{
//           width: 400,
//           p: 3,
//           bgcolor: "white",
//           borderRadius: 2,
//           mx: "auto",
//           mt: "20%",
//         }}
//       >
//         <Typography variant="h6" sx={{ mb: 2 }}>
//           {paymentSuccess ? "Success!" : "Transfer Payment"}
//         </Typography>

//         <Alert severity="info" sx={{ mb: 2 }}>
//           Total for {numListings} listing{numListings > 1 ? "s" : ""}:{" "}
//           <strong>${price.toFixed(2)}</strong>
//           {" "} (Base ${BASE_PRICE.toFixed(2)} + ${EXTRA_UNIT_PRICE.toFixed(2)} each additional)
//         </Alert>

//         <TextField
//           fullWidth
//           label="Billing Email"
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           sx={{ mb: 2 }}
//           required
//           disabled
//         />

//         {!requiresCard ? (
//           <Alert severity="success" sx={{ mt: 2 }}>
//             Payment successful! Charged automatically.
//           </Alert>
//         ) : (
//           <>
//             <CardElement
//               options={{ hidePostalCode: true }}
//               onChange={(e) => setCardComplete(e.complete)}
//             />
//             <Button
//               variant="contained"
//               fullWidth
//               onClick={handlePayment}
//               disabled={loading || !clientSecret || !cardComplete}
//               sx={{ mt: 2 }}
//             >
//               {loading ? (
//                 <CircularProgress size={24} />
//               ) : (
//                 `Pay $${price.toFixed(2)} & Save Card`
//               )}
//             </Button>
//             {error && (
//               <Alert severity="error" sx={{ mt: 2 }}>
//                 {error}
//               </Alert>
//             )}
//           </>
//         )}
//       </Box>
//     </Modal>
//   );
// };

// export default TransferPaymentModal;
